import { supabase } from './supabaseClient';

export async function loginUser(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  
  // Vérifier si l'utilisateur est bloqué
  const user = data.user;
  if (user) {
    console.log('Vérification du blocage lors de la connexion');
    
    // Vérifier d'abord directement dans la table students par email
    const { data: student } = await supabase
      .from('students')
      .select('blocked')
      .eq('email', email)
      .single();
    
    if (student && student.blocked) {
      console.log('Utilisateur bloqué détecté lors de la connexion');
      // Déconnecter l'utilisateur s'il est bloqué
      await supabase.auth.signOut();
      throw new Error('Votre compte a été bloqué par un administrateur. Veuillez contacter l\'administration.');
    }
    
    // Vérifier aussi avec la fonction checkIfUserIsBlocked
    const isBlocked = await checkIfUserIsBlocked(user.id);
    if (isBlocked) {
      console.log('Utilisateur bloqué détecté par checkIfUserIsBlocked');
      // Déconnecter l'utilisateur s'il est bloqué
      await supabase.auth.signOut();
      throw new Error('Votre compte a été bloqué par un administrateur. Veuillez contacter l\'administration.');
    }
  }
  
  return user;
}

export async function loginStudent(email: string, password: string) {
  return loginUser(email, password);
}

export async function loginTeacher(email: string, password: string) {
  return loginUser(email, password);
}

export async function loginAdmin(email: string, password: string) {
  return loginUser(email, password);
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  
  // Vérifier si l'utilisateur est bloqué
  if (user) {
    const isBlocked = await checkIfUserIsBlocked(user.id);
    if (isBlocked) {
      // Déconnecter l'utilisateur s'il est bloqué
      await supabase.auth.signOut();
      throw new Error('Votre compte a été bloqué par un administrateur. Veuillez contacter l\'administration.');
    }
  }
  
  return user;
}

export async function getUserProfile(userId: string) {
  // Essayer de récupérer le profil depuis une table 'profiles'
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error && error.code !== 'PGRST116') {
    console.error('Erreur lors de la récupération du profil:', error);
  }
  
  return profile;
}

export async function getUserRoles(userId: string): Promise<string[]> {
  try {
    console.log('Recherche des rôles pour utilisateur');
    
    // Essayer de récupérer les rôles depuis la table profiles
    const profile = await getUserProfile(userId);
    
    if (profile && profile.roles && Array.isArray(profile.roles)) {
      console.log('Rôles multiples trouvés dans profiles');
      return profile.roles;
    }
    
    // Fallback vers l'ancien système avec un seul rôle
    if (profile && profile.role) {
      console.log('Rôle unique trouvé dans profiles');
      return [profile.role];
    }
    
    // Si pas de profil, essayer de récupérer depuis les métadonnées
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user && user.user_metadata && user.user_metadata.role) {
      console.log('Rôle trouvé dans metadata');
      return [user.user_metadata.role];
    }
    
    // Par défaut, retourner 'student'
    console.log('Aucun rôle trouvé, utilisation du rôle par défaut: student');
    return ['student'];
  } catch (error) {
    console.error('Erreur lors de la récupération des rôles:', error);
    // En cas d'erreur, retourner 'student' par défaut
    return ['student'];
  }
}

export async function getUserRole(userId: string) {
  const roles = await getUserRoles(userId);
  return roles.length > 0 ? roles[0] : 'student';
}

export async function getStudentInfo(email: string) {
  try {
    console.log('Récupération des informations étudiant');
    
    // Vérifier si l'étudiant est bloqué directement par email
    const { data: student, error } = await supabase
      .from('students')
      .select('*')
      .eq('email', email)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Erreur lors de la récupération des infos étudiant:', error);
    }
    
    // Si l'étudiant est bloqué, lancer une erreur
    if (student && student.blocked) {
      console.log('Étudiant bloqué détecté dans getStudentInfo');
      throw new Error('Votre compte a été bloqué par un administrateur. Veuillez contacter l\'administration.');
    }
    
    // Vérifier aussi si l'utilisateur actuel est bloqué
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const isBlocked = await checkIfUserIsBlocked(user.id);
      if (isBlocked) {
        throw new Error('Votre compte a été bloqué par un administrateur. Veuillez contacter l\'administration.');
      }
    }
    
    return student;
  } catch (error) {
    console.error('Erreur lors de la récupération des infos étudiant:', error);
    return null;
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// Fonctions pour les soumissions de devoirs
export async function getGlobalSubmissionDeadline() {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', 'submission_deadline')
      .single();

    if (error && error.code === 'PGRST116') {
      // Pas de deadline configurée
      return null;
    }
    if (error) throw error;

    return data?.value || null;
  } catch (error) {
    console.error('Erreur lors de la récupération de la deadline globale:', error);
    return null;
  }
}

export async function setGlobalSubmissionDeadline(deadline: string | null) {
  try {
    if (!deadline) {
      // Supprimer la deadline
      const { error } = await supabase
        .from('app_config')
        .delete()
        .eq('key', 'submission_deadline');
      if (error) throw error;
      return null;
    }

    // Insérer ou mettre à jour la deadline
    const { data, error } = await supabase
      .from('app_config')
      .upsert({ key: 'submission_deadline', value: deadline })
      .select()
      .single();

    if (error) throw error;
    return data?.value || null;
  } catch (error) {
    console.error('Erreur lors de la définition de la deadline globale:', error);
    throw error;
  }
}

export async function getAssignmentById(assignmentId: number) {
  try {
    const { data: assignment, error } = await supabase
      .from('assignments')
      .select('*')
      .eq('id', assignmentId)
      .single();

    if (error) throw error;
    return assignment;
  } catch (error) {
    console.error('Erreur lors de la récupération du devoir:', error);
    return null;
  }
}

export async function createAssignment(assignmentData: {
  title: string;
  course: string;
  description?: string;
  due_date?: string;
  deadline?: string;
  points?: number;
  is_active?: boolean;
}) {
  try {
    const { data, error } = await supabase
      .from('assignments')
      .insert([{
        ...assignmentData,
        is_active: assignmentData.is_active !== false
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la création du devoir:', error);
    throw error;
  }
}

// Mettre à jour la date de soumission d'un devoir
export async function updateSubmissionDate(submissionId: number, newDate: string) {
  try {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .update({ submitted_at: newDate })
      .eq('id', submissionId);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la date de soumission:', error);
    throw error;
  }
}

export async function updateAssignment(assignmentId: number, assignmentData: Partial<{
  title: string;
  course: string;
  description: string;
  due_date: string;
  deadline: string;
  points: number;
  is_active: boolean;
}>) {
  try {
    const { data, error } = await supabase
      .from('assignments')
      .update(assignmentData)
      .eq('id', assignmentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du devoir:', error);
    throw error;
  }
}

export async function submitAssignment(
  assignmentId: number,
  studentId: string,
  file: File,
  title: string,
  comments?: string,
  onProgress?: (percent: number, speedMBs?: number) => void
) {
  try {
    // Vérifier la deadline GLOBALE
    const globalDeadline = await getGlobalSubmissionDeadline();
    if (globalDeadline) {
      const deadline = new Date(globalDeadline);
      const now = new Date();
      if (now > deadline) {
        throw new Error(`Délai de soumission de tous les devoirs dépassé. La date limite était le ${deadline.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`);
      }
    }

    const submissionId = `${assignmentId}_${Date.now()}`;

    // 1. Obtenir une URL pré-signée depuis le Worker Cloudflare
    onProgress?.(5);
    const { url: uploadUrl, key: r2Key } = await getUploadUrl(
      file.name,
      file.type || 'application/zip',
      submissionId
    );

    // 2. Uploader directement vers R2 avec suivi de progression et vitesse via XHR
    onProgress?.(10, 0);
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let lastLoaded = 0;
      let lastTime = Date.now();
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = 10 + Math.round((e.loaded / e.total) * 85);
          const now = Date.now();
          const elapsed = (now - lastTime) / 1000;
          const speedMBs = elapsed > 0.2 ? (e.loaded - lastLoaded) / elapsed / (1024 * 1024) : undefined;
          if (elapsed > 0.2) { lastLoaded = e.loaded; lastTime = now; }
          onProgress(percent, speedMBs);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error("Échec de l'upload vers le stockage"));
      };
      xhr.onerror = () => reject(new Error('Erreur réseau pendant l\'upload'));
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/zip');
      xhr.send(file);
    });

    // 3. Enregistrer la soumission en base avec la clé R2
    const { data, error } = await supabase
      .from('assignment_submissions')
      .insert({
        assignment_id: assignmentId,
        student_id: studentId,
        file_url: r2Key,
        file_name: file.name,
        submission_title: title,
        submitted_at: new Date().toISOString(),
        comments: comments || '',
        status: 'submitted'
      });

    if (error) throw error;

    return { success: true, data, fileUrl: r2Key };
  } catch (error) {
    console.error('Erreur lors de la soumission du devoir:', error);
    throw error;
  }
}

export async function getSubmittedAssignmentIds(studentId: string): Promise<number[]> {
  const { data } = await supabase
    .from('assignment_submissions')
    .select('assignment_id')
    .eq('student_id', studentId);
  return (data || []).map((s: any) => s.assignment_id).filter((id: any) => id !== null && id !== undefined);
}

export async function getAssignmentSubmissions(studentId: string) {
  try {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        assignments (
          title,
          course,
          due_date,
          points
        )
      `)
      .eq('student_id', studentId)
      .order('submitted_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des soumissions:', error);
    throw error;
  }
}

// Fonction pour récupérer tous les étudiants
export async function getAllStudents() {
  try {
    const { data: students, error } = await supabase
      .from('students')
      .select('*')
      .order('nom_complet', { ascending: true });

    if (error) {
      throw error;
    }

    return students;
  } catch (error) {
    console.error('Erreur lors de la récupération des étudiants:', error);
    throw error;
  }
}

// Fonction pour récupérer tous les devoirs rendus
export async function getAllAssignmentSubmissions() {
  try {
    const { data: submissions, error } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        assignments (
          title,
          course,
          due_date,
          points
        ),
        students (
          nom_complet,
          email,
          matricule
        )
      `)
      .order('submitted_at', { ascending: false });

    if (error) {
      throw error;
    }

    return submissions;
  } catch (error) {
    console.error('Erreur lors de la récupération des soumissions:', error);
    throw error;
  }
}

// Fonction pour récupérer les cours assignés à un enseignant
export async function getTeacherCourses(teacherEmail: string) {
  try {
    const { data: courses, error } = await supabase
      .from('course_assignments')
      .select('*')
      .eq('teacher_email', teacherEmail)
      .eq('is_active', true)
      .order('assigned_at', { ascending: false });

    if (error) {
      throw error;
    }

    return courses;
  } catch (error) {
    console.error('Erreur lors de la récupération des cours enseignants:', error);
    throw error;
  }
}

// Fonction pour récupérer les étudiants inscrits à un cours
export async function getStudentsByCourse(courseName: string) {
  try {
    const { data: students, error } = await supabase
      .from('students')
      .select('*')
      .eq('niveau', 'MR-TDDEFTP-2')
      .order('nom_complet', { ascending: true });

    if (error) {
      throw error;
    }

    return students;
  } catch (error) {
    console.error('Erreur lors de la récupération des étudiants par cours:', error);
    throw error;
  }
}

// Fonction pour récupérer les étudiants qui ont téléchargé un cours spécifique
export async function getStudentsWhoDownloadedCourse(courseName: string) {
  try {
    const { data: downloads, error } = await supabase
      .from('course_downloads')
      .select('student_email')
      .eq('course_name', courseName);

    if (error) {
      throw error;
    }

    if (downloads && downloads.length > 0) {
      const studentEmails = downloads.map(d => d.student_email);
      const { data: students, error: studentsError } = await supabase
        .from('students')
        .select('*')
        .in('email', studentEmails)
        .order('nom_complet', { ascending: true });

      if (studentsError) {
        throw studentsError;
      }

      return students;
    }

    return [];
  } catch (error) {
    console.error('Erreur lors de la récupération des étudiants ayant téléchargé le cours:', error);
    throw error;
  }
}

// Fonction pour envoyer un message aux étudiants d'un cours
export async function sendMessageToStudents(teacherEmail: string, courseName: string, messageTitle: string, messageContent: string) {
  try {
    // 1. Créer le message
    const { data: message, error: messageError } = await supabase
      .from('teacher_messages')
      .insert({
        teacher_email: teacherEmail,
        course_name: courseName,
        message_title: messageTitle,
        message_content: messageContent
      })
      .select()
      .single();

    if (messageError) {
      throw messageError;
    }

    // 2. Récupérer tous les étudiants Master 2
    const students = await getStudentsByCourse(courseName);

    // 3. Créer les notifications pour chaque étudiant
    if (students && students.length > 0) {
      const notifications = students.map(student => ({
        student_email: student.email,
        message_id: message.id
      }));

      const { error: notificationError } = await supabase
        .from('student_notifications')
        .insert(notifications);

      if (notificationError) {
        throw notificationError;
      }
    }

    return { success: true, messageId: message.id, studentsCount: students?.length || 0 };
  } catch (error) {
    console.error('Erreur lors de l\'envoi du message:', error);
    throw error;
  }
}

// Fonction pour récupérer les notifications d'un étudiant
export async function getStudentNotifications(studentEmail: string) {
  try {
    const { data: notifications, error } = await supabase
      .from('student_notifications')
      .select(`
        *,
        teacher_messages (
          id,
          teacher_email,
          course_name,
          message_title,
          message_content,
          created_at
        )
      `)
      .eq('student_email', studentEmail)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return notifications;
  } catch (error) {
    console.error('Erreur lors de la récupération des notifications:', error);
    throw error;
  }
}

// Fonction pour marquer une notification comme lue
export async function markNotificationAsRead(notificationId: number) {
  try {
    const { error } = await supabase
      .from('student_notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Erreur lors du marquage de la notification:', error);
    throw error;
  }
}

// Fonction pour enregistrer un téléchargement de cours
export async function recordCourseDownload(studentEmail: string, courseName: string) {
  try {
    const { error } = await supabase
      .from('course_downloads')
      .upsert({
        student_email: studentEmail,
        course_name: courseName
      }, {
        onConflict: 'student_email,course_name'
      });

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement du téléchargement:', error);
    throw error;
  }
}

// Fonction pour envoyer un message à tous les étudiants Master 2
export async function sendMessageToAllStudents(teacherEmail: string, messageTitle: string, messageContent: string) {
  try {
    // 1. Créer le message
    const { data: message, error: messageError } = await supabase
      .from('teacher_messages')
      .insert({
        teacher_email: teacherEmail,
        course_name: 'Message général',
        message_title: messageTitle,
        message_content: messageContent
      })
      .select()
      .single();

    if (messageError) {
      throw messageError;
    }

    // 2. Récupérer tous les étudiants Master 2
    const { data: students, error: studentsError } = await supabase
      .from('students')
      .select('*')
      .eq('niveau', 'MR-TDDEFTP-2')
      .order('nom_complet', { ascending: true });

    if (studentsError) {
      throw studentsError;
    }

    // 3. Créer les notifications pour chaque étudiant
    if (students && students.length > 0) {
      const notifications = students.map(student => ({
        student_email: student.email,
        message_id: message.id
      }));

      const { error: notificationError } = await supabase
        .from('student_notifications')
        .insert(notifications);

      if (notificationError) {
        throw notificationError;
      }
    }

    return { success: true, messageId: message.id, studentsCount: students?.length || 0 };
  } catch (error) {
    console.error('Erreur lors de l\'envoi du message à tous les étudiants:', error);
    throw error;
  }
}

// Nouvelles fonctions pour le Super Admin Dashboard

// Récupérer tous les administrateurs
export async function getAllAdmins() {
  try {
    const { data: admins, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'admin')
      .order('email', { ascending: true });
    
    if (error) {
      throw error;
    }
    return admins;
  } catch (error) {
    console.error('Erreur lors de la récupération des administrateurs:', error);
    throw error;
  }
}

// Récupérer tous les enseignants
export async function getAllTeachers() {
  try {
    const { data: teachers, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'teacher')
      .order('email', { ascending: true });
    
    if (error) {
      throw error;
    }
    return teachers;
  } catch (error) {
    console.error('Erreur lors de la récupération des enseignants:', error);
    throw error;
  }
}

// Récupérer tous les cours assignés par master
export async function getAllCoursesByMaster() {
  try {
    console.log('Récupération des cours assignés...');
    
    const { data: courses, error } = await supabase
      .from('course_assignments')
      .select(`
        id,
        teacher_email,
        course_name,
        created_at,
        is_active
      `)
      .order('course_name', { ascending: true });
    
    if (error) {
      throw error;
    }
    return courses;
  } catch (error) {
    console.error('Erreur lors de la récupération des cours par master:', error);
    throw error;
  }
}

// Récupérer les statistiques globales
export async function getGlobalStats() {
  try {
    // Compter les étudiants
    const { count: studentsCount, error: studentsError } = await supabase
      .from('students')
      .select('*', { count: 'exact', head: true });
    
    if (studentsError) throw studentsError;

    // Compter les enseignants
    const { count: teachersCount, error: teachersError } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'teacher');
    
    if (teachersError) throw teachersError;

    // Compter les administrateurs
    const { count: adminsCount, error: adminsError } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'admin');
    
    if (adminsError) throw adminsError;

    // Compter les soumissions de devoirs
    const { count: submissionsCount, error: submissionsError } = await supabase
      .from('assignment_submissions')
      .select('*', { count: 'exact', head: true });
    
    if (submissionsError) throw submissionsError;

    return {
      studentsCount: studentsCount || 0,
      teachersCount: teachersCount || 0,
      adminsCount: adminsCount || 0,
      submissionsCount: submissionsCount || 0
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    throw error;
  }
}

// Créer un nouvel administrateur
export async function createAdmin(email: string, password: string) {
  try {
    // Créer l'utilisateur dans auth.users
    const { data: authData, error: authError } = await supabase.auth.admin.createUser({
      email,
      password,
      email_confirm: true
    });

    if (authError) throw authError;

    // Ajouter le rôle admin dans profiles
    if (authData.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email: email,
          role: 'admin'
        });

      if (profileError) throw profileError;
    }

    return authData.user;
  } catch (error) {
    console.error('Erreur lors de la création de l\'administrateur:', error);
    throw error;
  }
}

// Créer un nouvel enseignant
export async function createTeacher(email: string, password: string, nom_complet?: string) {
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { role: 'teacher', nom_complet: nom_complet || null } }
  });
  if (authError) throw authError;
  if (!authData.user) throw new Error('Création du compte échouée');

  // Fallback : force le bon rôle si le trigger a inscrit 'student' par défaut
  await supabase
    .from('profiles')
    .update({ role: 'teacher', nom_complet: nom_complet || null })
    .eq('id', authData.user.id);

  return authData.user;
}

// Assigner un cours à un enseignant
export async function assignCourseToTeacher(teacherEmail: string, courseName: string) {
  try {
    const { data, error } = await supabase
      .from('course_assignments')
      .insert({
        teacher_email: teacherEmail,
        course_name: courseName
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de l\'assignation du cours:', error);
    throw error;
  }
}

// Supprimer l'assignation d'un cours
export async function removeCourseAssignment(assignmentId: number) {
  try {
    const { error } = await supabase
      .from('course_assignments')
      .delete()
      .eq('id', assignmentId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'assignation:', error);
    throw error;
  }
}

// Vérifier si un utilisateur est bloqué
export async function checkIfUserIsBlocked(userId: string): Promise<boolean> {
  try {
    console.log('Vérification du blocage pour utilisateur');
    
    // D'abord, récupérer l'email de l'utilisateur depuis profiles
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('email, blocked')
      .eq('id', userId)
      .single();
    
    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Erreur lors de la récupération du profil:', profileError);
    }
    
    // Si on a trouvé le profil et qu'il est bloqué
    if (profile && profile.blocked) {
      console.log('Utilisateur bloqué dans profiles');
      return true;
    }
    
    // Si on a l'email, vérifier aussi dans la table students
    if (profile && profile.email) {
      const { data: student } = await supabase
        .from('students')
        .select('blocked')
        .eq('email', profile.email)
        .single();
      
      if (student && student.blocked) {
        console.log('Étudiant bloqué dans students');
        return true;
      }
    }
    
    // Vérifier aussi directement dans students si on a l'email
    const { data: { user } } = await supabase.auth.getUser();
    if (user && user.email) {
      const { data: studentByEmail } = await supabase
        .from('students')
        .select('blocked')
        .eq('email', user.email)
        .single();
      
      if (studentByEmail && studentByEmail.blocked) {
        console.log('Étudiant bloqué par email');
        return true;
      }
    }
    
    console.log('Utilisateur non bloqué');
    return false;
  } catch (error) {
    console.error('Erreur lors de la vérification du statut de blocage:', error);
    return false;
  }
}

// Bloquer/Débloquer un utilisateur
export async function toggleUserBlock(userId: string, blocked: boolean) {
  try {
    console.log('Changement de statut utilisateur');
    
    // Essayer d'abord de mettre à jour dans la table students
    const { error: studentError } = await supabase
      .from('students')
      .update({ blocked })
      .eq('id', userId)
      .select();

    if (!studentError) {
      console.log('Mise à jour réussie dans la table students');
      return true;
    }

    // Si ça ne marche pas, essayer dans la table profiles
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ blocked })
      .eq('id', userId)
      .select();

    if (!profileError) {
      console.log('Mise à jour réussie dans la table profiles');
      return true;
    }

    // Si les deux échouent, essayer avec les métadonnées auth
    console.log('Tentative avec les métadonnées auth...');
    const { error: authError } = await supabase.auth.admin.updateUserById(userId, {
      user_metadata: { blocked }
    });
    
    if (authError) {
      console.error('Toutes les méthodes ont échoué');
      throw new Error('Impossible de mettre à jour le statut de blocage');
    }
    
    console.log('Changement de statut réussi avec les métadonnées auth');
    return true;
  } catch (error) {
    console.error('Erreur lors du changement de statut utilisateur:', error);
    throw error;
  }
}

// Envoyer un message à tous les utilisateurs (fonctionnalité admin)
export async function sendMessageToAllUsers(adminEmail: string, messageTitle: string, messageContent: string, targetRole?: string) {
  try {
    console.log('Envoi de message à tous les utilisateurs');
    
    // 1. Créer le message
    const { data: message, error: messageError } = await supabase
      .from('teacher_messages')
      .insert({
        teacher_email: adminEmail,
        course_name: 'Message administratif',
        message_title: messageTitle,
        message_content: messageContent,
        target_type: 'broadcast'
      })
      .select()
      .single();

    if (messageError) {
      console.error('Erreur lors de la création du message:', messageError);
      throw messageError;
    }

    console.log('Message créé avec succès');

    // 2. Récupérer les utilisateurs cibles selon le rôle
    let users: any[] = [];
    
    if (targetRole === 'all_students' || !targetRole) {
      // Récupérer tous les étudiants
      const { data: students, error: studentsError } = await supabase
        .from('students')
        .select('email');
      
      if (studentsError) {
        console.error('Erreur lors de la récupération des étudiants:', studentsError);
        throw studentsError;
      }
      users = users.concat(students || []);
    }
    
    if (targetRole === 'all_teachers') {
      // Récupérer tous les enseignants
      const { data: teachers, error: teachersError } = await supabase
        .from('profiles')
        .select('email')
        .eq('role', 'teacher');
      
      if (teachersError) {
        console.error('Erreur lors de la récupération des enseignants:', teachersError);
        throw teachersError;
      }
      users = users.concat(teachers || []);
    }
    
    if (targetRole === 'all_admins') {
      // Récupérer tous les administrateurs
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('email')
        .eq('role', 'admin');
      
      if (adminsError) {
        console.error('Erreur lors de la récupération des administrateurs:', adminsError);
        throw adminsError;
      }
      users = users.concat(admins || []);
    }
    
    if (targetRole === 'all_users') {
      // Récupérer tous les utilisateurs (étudiants + enseignants + admins)
      const { data: students, error: studentsError } = await supabase
        .from('students')
        .select('email');
      
      const { data: teachers, error: teachersError } = await supabase
        .from('profiles')
        .select('email')
        .eq('role', 'teacher');
      
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('email')
        .eq('role', 'admin');
      
      if (studentsError || teachersError || adminsError) {
        console.error('Erreur lors de la récupération des utilisateurs');
        throw studentsError || teachersError || adminsError;
      }
      
      users = users.concat(students || [], teachers || [], admins || []);
    }

    console.log('Utilisateurs cibles trouvés:', users.length);

    // 3. Créer les notifications pour les utilisateurs
    if (users && users.length > 0) {
      const notifications = users.map(user => ({
        student_email: user.email,
        message_id: message.id
      }));

      const { error: notificationError } = await supabase
        .from('student_notifications')
        .insert(notifications);

      if (notificationError) {
        console.error('Erreur lors de la création des notifications:', notificationError);
        throw notificationError;
      }
    }

    console.log('Message envoyé avec succès à', users.length, 'utilisateurs');
    return { success: true, messageId: message.id, usersCount: users.length };
  } catch (error) {
    console.error('Erreur lors de l\'envoi du message à tous les utilisateurs:', error);
    throw error;
  }
}

// Fonction pour récupérer tous les devoirs disponibles
export async function getAllAssignments() {
  try {
    const { data: assignments, error } = await supabase
      .from('assignments')
      .select('*')
      .eq('is_active', true)
      .order('due_date', { ascending: true });

    if (error) {
      throw error;
    }

    return assignments;
  } catch (error) {
    console.error('Erreur lors de la récupération des devoirs:', error);
    throw error;
  }
}

// Fonction pour récupérer les devoirs d'un cours spécifique
export async function getAssignmentsByCourse(courseName: string) {
  try {
    const { data: assignments, error } = await supabase
      .from('assignments')
      .select('*')
      .eq('course', courseName)
      .eq('is_active', true)
      .order('due_date', { ascending: true });

    if (error) {
      throw error;
    }

    return assignments;
  } catch (error) {
    console.error('Erreur lors de la récupération des devoirs du cours:', error);
    throw error;
  }
}

// Fonction pour récupérer les soumissions de devoirs par cours (pour les enseignants)
export async function getSubmissionsByCourse(courseName: string) {
  try {
    console.log('Recherche des soumissions pour le cours');
    
    // D'abord, récupérer les IDs des devoirs pour ce cours
    const { data: assignments, error: assignmentsError } = await supabase
      .from('assignments')
      .select('id')
      .eq('course', courseName);

    if (assignmentsError) {
      throw assignmentsError;
    }

    if (!assignments || assignments.length === 0) {
      console.log('Aucun devoir trouvé pour ce cours');
      return [];
    }

    // Extraire les IDs des devoirs
    const assignmentIds = assignments.map(assignment => assignment.id);

    // Ensuite, récupérer les soumissions pour ces devoirs
    const { data: submissions, error: submissionsError } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        assignments (
          title,
          course,
          due_date,
          points
        )
      `)
      .in('assignment_id', assignmentIds)
      .order('submitted_at', { ascending: false });

    if (submissionsError) {
      throw submissionsError;
    }

    return submissions;
  } catch (error) {
    console.error('Erreur lors de la récupération des soumissions par cours:', error);
    throw error;
  }
}

// Fonction pour récupérer les soumissions des cours assignés à un enseignant
export async function getSubmissionsForTeacher(teacherEmail: string) {
  try {
    console.log('Recherche des soumissions pour l\'enseignant');
    
    // D'abord, récupérer les cours assignés à l'enseignant
    const { data: assignedCourses, error: coursesError } = await supabase
      .from('course_assignments')
      .select('course_name')
      .eq('teacher_email', teacherEmail)
      .eq('is_active', true);

    if (coursesError) {
      throw coursesError;
    }

    if (!assignedCourses || assignedCourses.length === 0) {
      console.log('Aucun cours assigné');
      return [];
    }

    // Extraire les noms des cours
    const courseNames = assignedCourses.map(course => course.course_name);

    // Ensuite, récupérer les IDs des devoirs pour ces cours
    const { data: assignments, error: assignmentsError } = await supabase
      .from('assignments')
      .select('id')
      .in('course', courseNames);

    if (assignmentsError) {
      throw assignmentsError;
    }

    if (!assignments || assignments.length === 0) {
      console.log('Aucun devoir trouvé pour ces cours');
      return [];
    }

    // Extraire les IDs des devoirs
    const assignmentIds = assignments.map(assignment => assignment.id);

    // Ensuite, récupérer les soumissions pour ces devoirs
    const { data: submissions, error: submissionsError } = await supabase
      .from('assignment_submissions')
      .select('*')
      .in('assignment_id', assignmentIds)
      .order('submitted_at', { ascending: false });

    if (submissionsError) {
      throw submissionsError;
    }

    // Récupérer les détails des devoirs séparément
    const { data: assignmentsDetails, error: assignmentsDetailsError } = await supabase
      .from('assignments')
      .select('*')
      .in('id', assignmentIds);

    if (assignmentsDetailsError) {
      throw assignmentsDetailsError;
    }

    // Récupérer les informations des étudiants
    const studentIds = submissions?.map(s => s.student_id) || [];
    const { data: studentsInfo, error: studentsError } = await supabase
      .from('students')
      .select('id, nom_complet, matricule, email')
      .in('id', studentIds);

    if (studentsError) {
      console.error('Erreur lors de la récupération des informations étudiants:', studentsError);
    }

    // Combiner les soumissions avec les détails des devoirs et des étudiants
    const submissionsWithDetails = submissions?.map(submission => {
      const assignment = assignmentsDetails?.find(a => a.id === submission.assignment_id);
      const student = studentsInfo?.find(s => s.id === submission.student_id);
      return {
        ...submission,
        assignments: assignment,
        student: student
      };
    });

    return submissionsWithDetails || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des soumissions pour l\'enseignant:', error);
    throw error;
  }
}

// Fonction pour récupérer toutes les soumissions pour un enseignant (tous cours confondus)
export async function getAllSubmissionsForTeacher() {
  try {
    const { data: submissions, error } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        assignments (
          title,
          course,
          due_date,
          points
        )
      `)
      .order('submitted_at', { ascending: false });

    if (error) {
      throw error;
    }

    return submissions;
  } catch (error) {
    console.error('Erreur lors de la récupération de toutes les soumissions:', error);
    throw error;
  }
}

// Fonction pour envoyer un message individuel à un utilisateur spécifique
export async function sendIndividualMessage(adminEmail: string, recipientEmail: string, messageTitle: string, messageContent: string) {
  try {
    console.log('Envoi de message individuel');
    
    // Vérifier d'abord si le destinataire existe
    const { data: recipient, error: recipientError } = await supabase
      .from('profiles')
      .select('id, email, role')
      .eq('email', recipientEmail)
      .single();
    
    if (recipientError && recipientError.code !== 'PGRST116') {
      console.error('Erreur lors de la vérification du destinataire:', recipientError);
      throw new Error('Destinataire non trouvé');
    }
    
    // Si le destinataire n'est pas dans profiles, vérifier dans students
    if (!recipient) {
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('id, email')
        .eq('email', recipientEmail)
        .single();
      
      if (studentError && studentError.code !== 'PGRST116') {
        console.error('Erreur lors de la vérification de l\'étudiant:', studentError);
        throw new Error('Destinataire non trouvé');
      }
      
      if (!student) {
        throw new Error('Destinataire non trouvé');
      }
    }
    
    // Insérer le message dans la table teacher_messages
    const { data: message, error: messageError } = await supabase
      .from('teacher_messages')
      .insert({
        teacher_email: adminEmail,
        course_name: null, // null pour les messages individuels
        message_title: messageTitle,
        message_content: messageContent,
        target_type: 'individual',
        target_email: recipientEmail,
        created_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (messageError) {
      console.error('Erreur lors de l\'insertion du message:', messageError);
      throw messageError;
    }
    
    // Créer une notification pour le destinataire
    const { error: notificationError } = await supabase
      .from('student_notifications')
      .insert({
        student_email: recipientEmail,
        message_id: message.id
      });
    
    if (notificationError) {
      console.error('Erreur lors de la création de la notification:', notificationError);
      // Ne pas faire échouer l'envoi du message si la notification échoue
    }
    
    console.log('Message individuel envoyé avec succès');
    return message;
  } catch (error) {
    console.error('Erreur lors de l\'envoi du message individuel:', error);
    throw error;
  }
}

// Fonction pour récupérer toutes les soumissions (pour les admins)
// Obtenir le nombre total d'assignments pour une année donnée
export async function getTotalAssignmentsByYear(anneeEtude: number) {
  try {
    const { data: assignments, error } = await supabase
      .from('assignments')
      .select('id', { count: 'exact' })
      .eq('annee_etude', anneeEtude)
      .eq('is_active', true);

    if (error) {
      console.log('Fallback: Aucune table assignments trouvée, utilisant historique des soumissions');
      return null;
    }

    return assignments?.length || 1;
  } catch (error) {
    console.log('Fallback: Pas de table assignments, utilisant historique');
    return null;
  }
}

export async function getAllSubmissions() {
  try {
    // Récupérer toutes les soumissions
    const { data: submissions, error } = await supabase
      .from('assignment_submissions')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) throw error;

    if (!submissions || submissions.length === 0) {
      return [];
    }

    // Récupérer les IDs d'étudiants uniques
    const studentIds = Array.from(new Set(submissions.map(s => s.student_id)));

    if (studentIds.length === 0) {
      return submissions.map(s => ({ ...s, students: null, assignments: null }));
    }

    // Récupérer les infos des étudiants
    const { data: studentsInfo, error: studentsError } = await supabase
      .from('students')
      .select('id, nom_complet, matricule, email, niveau, annee_etude, annee_academique')
      .in('id', studentIds);

    if (studentsError) {
      console.error('Erreur lors de la récupération des étudiants:', studentsError);
    }

    // Combiner soumissions + infos étudiants
    const submissionsWithDetails = submissions.map(submission => {
      const student = (studentsInfo || []).find(s => s.id === submission.student_id);
      return {
        ...submission,
        assignments: null,
        students: student || null
      };
    });

    return submissionsWithDetails;
  } catch (error) {
    console.error('Erreur lors de la récupération de toutes les soumissions:', error);
    throw error;
  }
}

// ===== FONCTIONS POUR LA GESTION DE LA PROGRESSION ACADÉMIQUE =====

// Fonction pour promouvoir un étudiant en année supérieure
export async function promoteStudent(studentEmail: string) {
  try {
    console.log('Promotion de l\'étudiant:', studentEmail);
    
    // Appeler la fonction PostgreSQL pour promouvoir l'étudiant
    const { data, error } = await supabase.rpc('promote_student', {
      student_email: studentEmail
    });
    
    if (error) {
      console.error('Erreur lors de la promotion:', error);
      throw error;
    }
    
    console.log('Promotion réussie:', data);
    return data;
  } catch (error) {
    console.error('Erreur lors de la promotion de l\'étudiant:', error);
    throw error;
  }
}

// Fonction pour rétrograder un étudiant (utilisée pour les erreurs)
export async function demoteStudent(studentEmail: string) {
  try {
    console.log('Rétrogradation de l\'étudiant:', studentEmail);
    
    // Appeler la fonction PostgreSQL pour rétrograder l'étudiant
    const { data, error } = await supabase.rpc('demote_student', {
      student_email: studentEmail
    });
    
    if (error) {
      console.error('Erreur lors de la rétrogradation:', error);
      throw error;
    }
    
    console.log('Rétrogradation réussie:', data);
    return data;
  } catch (error) {
    console.error('Erreur lors de la rétrogradation de l\'étudiant:', error);
    throw error;
  }
}

// Fonction pour récupérer les étudiants avec leurs années d'étude
export async function getStudentsWithStudyYear() {
  try {
    console.log('Récupération des étudiants avec année d\'étude...');
    
    const { data: students, error } = await supabase
      .from('students')
      .select('*')
      .order('nom_complet', { ascending: true });
    
    if (error) {
      throw error;
    }
    
    console.log('Étudiants récupérés:', students?.length || 0);
    return students || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des étudiants avec année d\'étude:', error);
    throw error;
  }
}

// Fonction pour promouvoir plusieurs étudiants en lot
export async function promoteStudents(studentEmails: string[]) {
  try {
    console.log('Promotion en lot de', studentEmails.length, 'étudiants');
    
    const results = [];
    for (const email of studentEmails) {
      try {
        const result = await promoteStudent(email);
        results.push({ email, success: true, message: result });
      } catch (error) {
        results.push({ 
          email, 
          success: false, 
          message: `Erreur: ${error}` 
        });
      }
    }
    
    console.log('Résultats de la promotion en lot:', results);
    return results;
  } catch (error) {
    console.error('Erreur lors de la promotion en lot:', error);
    throw error;
  }
} 
// ─── Paiements FedaPay ───────────────────────────────────────────────────────

export interface PaymentRecord {
  id: string;
  student_id?: string;
  student_email: string;
  student_name: string;
  matricule?: string;
  type: 'scolarite' | 'laboratoire';
  amount: number;
  description: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  fedapay_transaction_id?: string;
  fedapay_reference?: string;
  created_at: string;
  updated_at: string;
}

export async function createPayment(data: Omit<PaymentRecord, 'id' | 'created_at' | 'updated_at'>): Promise<PaymentRecord> {
  // Exclure student_id : uuid Supabase incompatible avec l'id étudiant (email/int)
  // student_email suffit comme identifiant unique.
  const { student_id: _ignored, ...insertData } = data;
  const { data: record, error } = await supabase
    .from('payments')
    .insert([insertData])
    .select()
    .single();
  if (error) throw error;
  return record;
}

export async function updatePaymentStatus(
  id: string,
  update: { status: string; fedapay_transaction_id?: string; fedapay_reference?: string }
): Promise<void> {
  const { error } = await supabase
    .from('payments')
    .update({ ...update, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function getStudentPayments(studentEmail: string): Promise<PaymentRecord[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('student_email', studentEmail)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getAllPayments(): Promise<PaymentRecord[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ─── Preuves de paiement ─────────────────────────────────────────────────────

export interface PaymentProof {
  id: string;
  student_email: string;
  student_name: string;
  matricule?: string;
  type: 'scolarite' | 'laboratoire';
  proof_url: string;
  uploaded_at: string;
}

export async function uploadPaymentProof(
  file: File,
  studentEmail: string,
  studentName: string,
  matricule: string | undefined,
  type: 'scolarite' | 'laboratoire'
): Promise<PaymentProof> {
  const ext = file.name.split('.').pop() || 'png';
  const path = `${type}/${studentEmail.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('payment-proofs')
    .upload(path, file, { upsert: false });
  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('payment-proofs')
    .getPublicUrl(path);
  const proof_url = urlData.publicUrl;

  const { data: record, error: dbError } = await supabase
    .from('payment_proofs')
    .insert([{ student_email: studentEmail, student_name: studentName, matricule, type, proof_url }])
    .select()
    .single();
  if (dbError) throw dbError;
  return record;
}

export async function getStudentPaymentProofs(studentEmail: string): Promise<PaymentProof[]> {
  const { data, error } = await supabase
    .from('payment_proofs')
    .select('*')
    .eq('student_email', studentEmail)
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getAllPaymentProofs(): Promise<PaymentProof[]> {
  const { data, error } = await supabase
    .from('payment_proofs')
    .select('*')
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export interface Book {
  id: string;
  title: string;
  author: string;
  description: string;
  price: number;
  category: string;
  pages?: number;
  edition?: string;
  publisher?: string;
  published_year?: number;
  badge?: string;
  cover_url?: string;
  in_stock: boolean;
  created_at: string;
}

export async function getAllBooksAdmin(): Promise<Book[]> {
  const { data, error } = await supabase.from('books').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getBooks(): Promise<Book[]> {
  const { data, error } = await supabase.from('books').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createBook(bookData: Omit<Book, 'id' | 'created_at'>, coverFile?: File): Promise<Book> {
  let cover_url = bookData.cover_url;
  if (coverFile) {
    const ext = coverFile.name.split('.').pop();
    const filePath = `covers/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('book-covers').upload(filePath, coverFile);
    if (uploadError) throw uploadError;
    cover_url = supabase.storage.from('book-covers').getPublicUrl(filePath).data.publicUrl;
  }
  const { data, error } = await supabase.from('books').insert([{ ...bookData, cover_url }]).select().single();
  if (error) throw error;
  return data;
}

export async function updateBook(id: string, bookData: Partial<Omit<Book, 'id' | 'created_at'>>, coverFile?: File): Promise<Book> {
  let cover_url = bookData.cover_url;
  if (coverFile) {
    const ext = coverFile.name.split('.').pop();
    const filePath = `covers/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('book-covers').upload(filePath, coverFile);
    if (uploadError) throw uploadError;
    cover_url = supabase.storage.from('book-covers').getPublicUrl(filePath).data.publicUrl;
  }
  const { data, error } = await supabase.from('books').update({ ...bookData, cover_url }).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteBook(id: string): Promise<void> {
  const { error } = await supabase.from('books').delete().eq('id', id);
  if (error) throw error;
}

export interface BlogAuthor {
  name: string;
  affiliation?: string;
  email?: string;
}

export interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  authors: BlogAuthor[];
  abstract_fr?: string;
  abstract_en?: string;
  keywords_fr?: string[];
  keywords_en?: string[];
  content?: string;
  cover_url?: string;
  volume?: string;
  issue_number?: string;
  submitted_at?: string;
  published_at?: string;
  updated_at?: string;
  status: 'draft' | 'published';
  created_at?: string;
}

export async function getPublishedArticles(): Promise<BlogArticle[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('*')
    .eq('status', 'published')
    .order('published_at', { ascending: false });
  if (error) throw error;
  return (data || []) as BlogArticle[];
}

export async function getAllBlogArticles(): Promise<BlogArticle[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as BlogArticle[];
}

export async function getArticleBySlug(slug: string): Promise<BlogArticle | null> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('*')
    .eq('slug', slug)
    .single();
  if (error) return null;
  return data as BlogArticle;
}

export async function createBlogArticle(article: Omit<BlogArticle, 'id' | 'created_at' | 'updated_at'>, coverFile?: File): Promise<BlogArticle> {
  let cover_url = article.cover_url;
  if (coverFile) {
    const ext = coverFile.name.split('.').pop();
    const filePath = `articles/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('blog-covers').upload(filePath, coverFile);
    if (uploadError) throw uploadError;
    cover_url = supabase.storage.from('blog-covers').getPublicUrl(filePath).data.publicUrl;
  }
  const { data, error } = await supabase.from('blog_articles').insert([{ ...article, cover_url }]).select().single();
  if (error) throw error;
  return data as BlogArticle;
}

export async function updateBlogArticle(id: string, article: Partial<Omit<BlogArticle, 'id' | 'created_at'>>, coverFile?: File): Promise<BlogArticle> {
  let cover_url = article.cover_url;
  if (coverFile) {
    const ext = coverFile.name.split('.').pop();
    const filePath = `articles/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('blog-covers').upload(filePath, coverFile);
    if (uploadError) throw uploadError;
    cover_url = supabase.storage.from('blog-covers').getPublicUrl(filePath).data.publicUrl;
  }
  const { data, error } = await supabase.from('blog_articles').update({ ...article, cover_url }).eq('id', id).select().single();
  if (error) throw error;
  return data as BlogArticle;
}

export async function deleteBlogArticle(id: string): Promise<void> {
  const { error } = await supabase.from('blog_articles').delete().eq('id', id);
  if (error) throw error;
}

const WORKER_URL = process.env.REACT_APP_WORKER_URL;

export async function getUploadUrl(
  filename: string,
  contentType: string,
  submissionId: string
): Promise<{ url: string; key: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${WORKER_URL}/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify({ filename, contentType, submissionId }),
  });
  if (!res.ok) throw new Error("Impossible d'obtenir l'URL d'upload");
  return await res.json();
}

export async function downloadSubmissionFile(key: string, filename?: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  // Le Worker génère une URL pré-signée — le téléchargement se fait directement depuis R2
  const res = await fetch(`${WORKER_URL}/download?key=${encodeURIComponent(key)}`, {
    headers: { Authorization: `Bearer ${session?.access_token}` },
  });
  if (!res.ok) throw new Error('Téléchargement impossible');
  const { url, filename: serverFilename } = await res.json();
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || serverFilename || key.split('/').pop() || 'devoir';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function listStudentFiles(studentId: string): Promise<{ key: string; size: number; uploaded: string; filename: string }[]> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${WORKER_URL}/list?studentId=${studentId}`, {
    headers: { Authorization: `Bearer ${session?.access_token}` },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.files || [];
}
